import * as React from 'react';
import { FC, useMemo } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { RotateIcon } from '@rbilabs/components-library/build/components/rotate-icon';
import { useIntl } from 'react-intl';

import ConfirmDialog from 'components/confirm-dialog';
import { InputWithButton } from 'components/input-with-button';
import { SavedPromotionsModal } from 'components/saved-promotions-modal';

import {
  Animated,
  CTAButtonText,
  CallToActionHeader,
  Divider,
  ErrorMessage,
  MessageContainer,
  OptionsContainer,
  PromoCTA,
  PromoExpandToggleIcon,
  SuccessCode,
  SuccessMessage,
  Wrapper,
} from './cart-promo-codes.styled';
import { ICartPromoCodesViewProps } from './types';

export const CartPromoCodesView: FC<ICartPromoCodesViewProps> = ({
  onSubmit,
  codeInput,
  isLoading,
  isExpanded,
  setIsExpanded,
  onChangeInput,
  errorMessageId,
  appliedOfferName,
  appliedOfferError,
  appliedOfferDescription,
  cartPromoCodeOffers,
  removeAppliedOffer,
  showSavedPromotionsModal,
  onSelectedSavedPromotion,
  updateSavedPromotionsModal,
  showConfirmSwitchOffersDialog,
  onConfirmSwitchOffers,
  onCancelSwitchOffers,
}) => {
  const { formatMessage } = useIntl();

  const haveAppliedCartPromoOffer = useMemo(
    () => appliedOfferName && appliedOfferDescription,
    [appliedOfferDescription, appliedOfferName]
  );

  const tabIndex = isExpanded ? 0 : -1;

  return (
    <Wrapper data-testid="cart-promo-code-container">
      <PromoCTA
        onClick={() => setIsExpanded(!isExpanded)}
        aria-label={formatMessage({ id: 'togglePromoCodeAria' })}
        data-testid="cart-promo-code-toggle-button"
      >
        <CallToActionHeader>{formatMessage({ id: 'promoCodeCTA' })}</CallToActionHeader>
        <CTAButtonText>{formatMessage({ id: 'togglePromoCode' })}</CTAButtonText>
        <PromoExpandToggleIcon>
          <RotateIcon active={isExpanded} toDeg={90}>
            <Icon icon="next" color="icon-button-secondary" width="16px" aria-hidden />
          </RotateIcon>
        </PromoExpandToggleIcon>
      </PromoCTA>
      <Animated
        pose={isExpanded ? 'open' : 'closed'}
        data-testid="cart-promo-code-input-animated"
        aria-hidden={!isExpanded}
      >
        <InputWithButton
          buttonLabel={formatMessage({ id: 'apply' })}
          label={formatMessage({ id: 'enterPromoCode' })}
          name="cartPromoCode"
          value={codeInput}
          onChange={onChangeInput}
          isLoading={isLoading}
          onSubmit={onSubmit}
          buttonTabIndex={tabIndex}
          tabIndex={tabIndex}
        />
        <MessageContainer data-testid="cart-promo-code-message-container">
          {errorMessageId && (
            <ErrorMessage data-testid="cart-promo-code-error-message">
              {formatMessage({ id: errorMessageId })}
            </ErrorMessage>
          )}
          {haveAppliedCartPromoOffer && (
            <>
              <SuccessCode data-testid="cart-promo-code-success-name">
                {appliedOfferName}
              </SuccessCode>
              <SuccessMessage data-testid="cart-promo-code-success-description">
                {appliedOfferDescription}
              </SuccessMessage>
              {appliedOfferError && (
                <ErrorMessage data-testid="cart-promo-code-offer-error-message">
                  {formatMessage({ id: 'cartPromotionOfferRequirementsNotMetError' })}
                </ErrorMessage>
              )}
            </>
          )}
        </MessageContainer>
        <OptionsContainer>
          <PromoCTA
            onClick={() => updateSavedPromotionsModal(true)}
            aria-label={formatMessage({ id: 'togglePromotionModalAria' })}
            data-testid="toggle-promotion-modal"
            tabIndex={tabIndex}
          >
            <CTAButtonText>{formatMessage({ id: 'togglePromotionModal' })}</CTAButtonText>
          </PromoCTA>
          {haveAppliedCartPromoOffer && (
            <PromoCTA
              onClick={() => removeAppliedOffer()}
              aria-label={formatMessage({ id: 'removeAppliedCartPromoCodeAriaLabel' })}
              data-testid="cart-promo-code-remove-button"
              tabIndex={tabIndex}
            >
              <CTAButtonText>{formatMessage({ id: 'remove' })}</CTAButtonText>
            </PromoCTA>
          )}
        </OptionsContainer>
        {showSavedPromotionsModal && (
          <SavedPromotionsModal
            onDismiss={() => updateSavedPromotionsModal(false, true)}
            onSelectPromotion={onSelectedSavedPromotion}
            cartPromoCodeOffers={cartPromoCodeOffers}
          />
        )}
        {showConfirmSwitchOffersDialog && (
          <ConfirmDialog
            heading={formatMessage({ id: 'oneOfferPerOrder' })}
            body={formatMessage({ id: 'replaceOffer' })}
            confirmLabel={formatMessage({ id: 'yes' })}
            cancelLabel={formatMessage({ id: 'no' })}
            onConfirm={onConfirmSwitchOffers}
            onCancel={onCancelSwitchOffers}
            onDismiss={onCancelSwitchOffers}
            modalAppearanceEventMessage="Confirm Replace Coupon"
          />
        )}
        <Divider />
      </Animated>
    </Wrapper>
  );
};
